import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { showAlert, showErrorAlert, showQuestionDeleteAlert } from "../../../components/molecules/ShowAlert";
import { actualizarGrupo, getOneGrupo } from "../../../services/grupo.service";
import { getAllDocentes } from "../../../services/docente.service";
import { getAllTurnos } from "../../../services/turno.service";
import { getAllModulos } from "../../../services/modulo.service";
import { getAllModalidades } from "../../../services/modalidad.service";
import { getAllHorarios } from "../../../services/horario.service";
import { crearHorarioGrupo, getAllHorarioGrupos, eliminarHorarioGrupo } from "../../../services/horarioGrupo";
import { NuevoHorarioFormCard } from "../../../modules/grupo/NuevoHorarioFormCard";

/* --- estilos --- */
const buttonStyle = {
  padding: "10px 20px",
  backgroundColor: "#38ad3cff",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};
const cancelButtonStyle = { ...buttonStyle, backgroundColor: "#f44336" };

/* -------- helpers -------- */

// Determina si un horario debe mostrarse (soft delete / inhabilitado fuera)
const isHorarioVisible = (hg) => {
  if (!hg || typeof hg !== "object") return false;

  // flags comunes
  if ("eliminado" in hg && hg.eliminado) return false;
  if ("fechaBaja" in hg && hg.fechaBaja) return false;
  if ("inhabilitado" in hg && hg.inhabilitado) return false;
  if ("habilitado" in hg && hg.habilitado === false) return false;
  if ("activo" in hg && hg.activo === false) return false;

  if ("estado" in hg && hg.estado != null) {
    const e = String(hg.estado).toUpperCase();
    if (["INHABILITADO", "ELIMINADO", "DESHABILITADO", "0", "FALSE", "F"].includes(e)) return false;
  }
  return true;
};

// Normaliza estructura para garantizar campos esperados
const normalizarHorario = (horario) => {
  if (horario?.horario) {
    return {
      idHorarioGrupo: horario.idHorarioGrupo,
      nroAula: horario.nroAula || "",
      idHorario: horario.idHorario,
      horario: {
        dia: horario.horario.dia || { nombre: "Sin día" },
        horaInicio: horario.horario.horaInicio || "?",
        horaFin: horario.horario.horaFin || "?",
      },
    };
  }
  return {
    idHorarioGrupo: horario.idHorarioGrupo,
    nroAula: horario.nroAula || "",
    idHorario: horario.idHorario,
    horario: { dia: { nombre: "Sin día" }, horaInicio: "?", horaFin: "?" },
  };
};

// Unique por idHorarioGrupo
const uniqueById = (arr) => Array.from(new Map(arr.map((x) => [x.idHorarioGrupo, x])).values());

export const ActualizarGrupoPage = () => {
  const { idGrupo } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    idGrupo: "",
    nombre: "",
    nombreAntGrupo: "",
    cupos: "",
    cuentaZoom: "",
    contrasenaZoom: "",
    nombreAuxiliar: "",
    celularAuxiliar: "",
    idTurno: "",
    idDocente: "",
    idModulo: "",
    idModalidad: "",
    fechaInicio: "",
    fechaFin: "",
  });

  const [turnos, setTurnos] = useState([]);
  const [docentesAux, setDocentesAux] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [modalidades, setModalidades] = useState([]);
  const [error, setError] = useState(null);
  const [nombreOriginal, setNombreOriginal] = useState("");
  const [loading, setLoading] = useState(true);

  const [horarios, setHorarios] = useState([]); // disponibles (select)
  const [horariosGrupoActuales, setHorariosGrupoActuales] = useState([]); // visibles en UI
  const [horariosGrupoIniciales, setHorariosGrupoIniciales] = useState([]); // baseline para diff
  const [deletingIds, setDeletingIds] = useState(new Set());

  const {
    control,
    register: registerHorarios,
    setValue,
    formState: { errors: errorsHorarios },
  } = useForm();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [turnosList, docentesList, modulosList, modalidadesList] = await Promise.all([
          getAllTurnos(),
          getAllDocentes(),
          getAllModulos(),
          getAllModalidades(),
        ]);

        setTurnos(turnosList);
        setModulos(modulosList);
        setModalidades(modalidadesList);

        const docentesInfo = docentesList.map((item) => ({
          idDocente: item.docente.idDocente,
          nombreCompleto: `${item.persona.nombre} ${item.persona.apellido}`,
        }));
        setDocentesAux(docentesInfo);

        const grupoData = await getOneGrupo(idGrupo);
        setNombreOriginal(grupoData?.nombre || "");
        setFormData({
          idGrupo: grupoData.idGrupo,
          nombre: grupoData.nombre || "",
          nombreAntGrupo: grupoData.nombreAntGrupo || "",
          cupos: grupoData.cupos ?? "",
          cuentaZoom: grupoData.cuentaZoom ?? "",
          contrasenaZoom: grupoData.contrasenaZoom ?? "",
          nombreAuxiliar: grupoData.nombreAuxiliar ?? "",
          celularAuxiliar: grupoData.celularAuxiliar ?? "",
          idTurno: grupoData.idTurno ?? "",
          idDocente: grupoData.idDocente ?? "",
          idModulo: grupoData.idModulo ?? "",
          idModalidad: grupoData.idModalidad ?? "",
          fechaInicio: grupoData.fechaInicio?.split("T")[0] ?? "",
          fechaFin: grupoData.fechaFin?.split("T")[0] ?? "",
        });

        await cargarHorariosGrupo();
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError("Error al cargar los datos del grupo.");
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idGrupo]);

  const cargarHorariosGrupo = async () => {
    try {
      // 1) Horarios disponibles (para el select)
      const horariosList = await getAllHorarios();
      const formattedHorarios = horariosList.map((h) => ({
        value: h.idHorario,
        label: `${h.dia?.nombre || "Sin día"} - ${h.horaInicio} - ${h.horaFin}`,
      }));
      setHorarios(formattedHorarios);

      // 2) Horarios del grupo (fuente principal: getOneGrupo)
      const grupoData = await getOneGrupo(idGrupo);
      let horariosGrupo = [];

      if (Array.isArray(grupoData?.horarios) && grupoData.horarios.length > 0) {
        horariosGrupo = grupoData.horarios.filter(isHorarioVisible).map(normalizarHorario);
      } else {
        // Fallback a todos y filtramos por grupo + visibles
        const todosHorariosGrupo = await getAllHorarioGrupos();
        horariosGrupo = todosHorariosGrupo
          .filter((hg) => hg.idGrupo === idGrupo)
          .filter(isHorarioVisible)
          .map(normalizarHorario);
      }

      // 3) Enriquecer con texto legible + evitar duplicados
      const enriquecidos = uniqueById(
        horariosGrupo.map((hg) => {
          const hSel = formattedHorarios.find((h) => h.value === hg.idHorario);
          if (hSel) {
            const [dia, horaInicio, horaFin] = hSel.label.split(" - ");
            return {
              ...hg,
              horario: {
                dia: { nombre: dia },
                horaInicio,
                horaFin,
              },
            };
          }
          return hg;
        })
      );

      setHorariosGrupoActuales(enriquecidos);
      setHorariosGrupoIniciales(enriquecidos);

      // 4) Llenar react-hook-form
      const horariosFormateados = enriquecidos.map((horario) => ({
        nroAula: horario.nroAula,
        idHorario: {
          value: horario.idHorario,
          label: `${horario.horario.dia.nombre} - ${horario.horario.horaInicio} - ${horario.horario.horaFin}`,
        },
        idHorarioGrupo: horario.idHorarioGrupo,
      }));
      setValue("horarioAulas", horariosFormateados);
    } catch (err) {
      console.error("Error al cargar horarios del grupo:", err);
      setHorariosGrupoActuales([]);
      setHorariosGrupoIniciales([]);
      setValue("horarioAulas", []);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const eliminarHorarioIndividual = (idHorarioGrupo) => {
    showQuestionDeleteAlert({
      title: "¿Eliminar horario?",
      text: "¿Estás seguro de que deseas eliminar este horario?",
      callback: async (result) => {
        if (!result.isConfirmed) return;

        // Eliminación optimista en UI
        const backup = horariosGrupoActuales;
        setDeletingIds((prev) => new Set(prev).add(idHorarioGrupo));
        setHorariosGrupoActuales((prev) => prev.filter((x) => x.idHorarioGrupo !== idHorarioGrupo));

        try {
          await eliminarHorarioGrupo(idHorarioGrupo);
          // Refrescamos desde backend (por si hay soft delete)
          await cargarHorariosGrupo();

          showAlert({
            title: "¡Horario eliminado!",
            text: "El horario ha sido eliminado correctamente.",
          });
        } catch (err) {
          console.error("Error al eliminar horario:", err);
          // Revertimos UI si falla
          setHorariosGrupoActuales(backup);
          showErrorAlert("Error al eliminar el horario. " + (err?.message || ""));
        } finally {
          setDeletingIds((prev) => {
            const copy = new Set(prev);
            copy.delete(idHorarioGrupo);
            return copy;
          });
        }
      },
    });
  };

  const handleSubmitHorarios = async () => {
    try {
      const formValues = control._formValues || {};
      const horariosData = formValues.horarioAulas || [];

      // ids que siguen en el form (persisten)
      const idsActuales = horariosData.filter((h) => h.idHorarioGrupo).map((h) => h.idHorarioGrupo);

      // detectar eliminados respecto a baseline visible
      const horariosAEliminar = horariosGrupoIniciales.filter(
        (h) => !idsActuales.includes(h.idHorarioGrupo)
      );

      await Promise.all(
        horariosAEliminar.map(async (hg) => {
          try {
            await eliminarHorarioGrupo(hg.idHorarioGrupo);
          } catch (e) {
            console.warn("Fallo eliminando horario:", hg.idHorarioGrupo, e);
          }
        })
      );

      // crear nuevos (los que no tienen idHorarioGrupo)
      const nuevos = horariosData.filter((h) => !h.idHorarioGrupo);
      await Promise.all(
        nuevos.map((horarioAula) => {
          const data = {
            nroAula: horarioAula.nroAula || "",
            idHorario: horarioAula.idHorario?.value || horarioAula.idHorario,
            idGrupo: idGrupo,
          };
          return crearHorarioGrupo(data);
        })
      );

      await cargarHorariosGrupo();

      showAlert({
        title: "¡Horarios actualizados!",
        text: "Los horarios del grupo han sido actualizados correctamente.",
      });
    } catch (err) {
      console.error("Error al gestionar horarios:", err);
      showErrorAlert("Error al actualizar los horarios: " + (err?.message || ""));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...formData,
        nombreAntGrupo: formData.nombre !== nombreOriginal ? nombreOriginal : formData.nombreAntGrupo,
      };

      await actualizarGrupo(formData.idGrupo, updateData);

      showAlert({
        title: "¡Actualizado correctamente!",
        text: "Los datos del grupo han sido actualizados correctamente.",
        callback: () => navigate("/grupos"),
      });
    } catch (err) {
      console.error("Error al actualizar:", err);
      showErrorAlert("Ocurrió un error al actualizar el grupo: " + (err?.message || ""));
    }
  };

  if (loading) {
    return (
      <ContenedorPageTemplate titulo="Cargando grupo...">
        <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </ContenedorPageTemplate>
    );
  }

  if (error) {
    return (
      <ContenedorPageTemplate titulo="Error">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-secondary mt-3" onClick={() => navigate("/grupos")}>
          Volver a grupos
        </button>
      </ContenedorPageTemplate>
    );
  }

  return (
    <ContenedorPageTemplate titulo={`Actualizar datos del Grupo: ${formData.nombre}`}>
      <div style={{ display: "flex", gap: "20px", maxWidth: "1200px", margin: "0 auto" }}>
        {/* --- Formulario principal --- */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          {/* Encabezado del formulario */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <h3 className="text-xl font-semibold text-gray-800">Datos del Grupo</h3>
            <p className="text-sm text-gray-500 mt-1">Complete la información necesaria del grupo</p>
          </div>

          <div className="p-6">
            <input type="hidden" name="nombreAntGrupo" value={formData.nombreAntGrupo} />

            <div className="space-y-6">
              {/* Información General */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Información General</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre anterior</label>
                    <input
                      type="text"
                      name="nombreAntGrupo"
                      value={formData.nombreAntGrupo}
                      onChange={handleChange}
                      placeholder="Opcional"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cupos</label>
                    <input
                      type="number"
                      name="cupos"
                      value={formData.cupos}
                      onChange={handleChange}
                      min="1"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Información de Zoom */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Información de Zoom</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cuenta Zoom</label>
                    <input
                      type="text"
                      name="cuentaZoom"
                      value={formData.cuentaZoom}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña Zoom</label>
                    <input
                      type="text"
                      name="contrasenaZoom"
                      value={formData.contrasenaZoom}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Información del Auxiliar */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Información del Auxiliar</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Auxiliar</label>
                    <input
                      type="text"
                      name="nombreAuxiliar"
                      value={formData.nombreAuxiliar}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Celular del Auxiliar</label>
                    <input
                      type="text"
                      name="celularAuxiliar"
                      value={formData.celularAuxiliar}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Información Académica */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Información Académica</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Turno</label>
                    <select
                      name="idTurno"
                      value={formData.idTurno}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      {turnos.map((tur) => (
                        <option key={tur.idTurno} value={tur.idTurno}>
                          {tur.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Docente</label>
                    <select
                      name="idDocente"
                      value={formData.idDocente}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Seleccione un docente</option>
                      {docentesAux.map((docente) => (
                        <option key={docente.idDocente} value={docente.idDocente}>
                          {docente.nombreCompleto}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Módulo</label>
                    <select
                      name="idModulo"
                      value={formData.idModulo}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Seleccione un módulo</option>
                      {modulos.map((mod) => (
                        <option key={mod.idModulo} value={mod.idModulo}>
                          {mod.nombre} ({mod.sigla})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Modalidad</label>
                    <select
                      name="idModalidad"
                      value={formData.idModalidad}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Seleccione una modalidad</option>
                      {modalidades.map((m) => (
                        <option key={m.idModalidad} value={m.idModalidad}>
                          {m.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Fechas */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Fechas del Grupo</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de inicio</label>
                    <input
                      type="date"
                      name="fechaInicio"
                      value={formData.fechaInicio}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de fin</label>
                    <input
                      type="date"
                      name="fechaFin"
                      value={formData.fechaFin}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Acciones del formulario */}
        <div
          className="mt-4"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}
        >
          <button
            type="submit"
            className="btn py-3"
            style={{ background: "#22c55e", color: "#fff", fontWeight: 600, border: "none", fontSize: "1.05rem", borderRadius: "8px" }}
          >Guardar cambios
          </button>

          <button
            type="button"
            onClick={() => navigate("/grupos")}
            className="btn py-3"
            style={{ background: "#ef4444", color: "#fff", fontWeight: 600, border: "none", fontSize: "1.05rem", borderRadius: "8px" }}
          >Cancelar
          </button>
        </div>
        </form>

        {/* --- Gestión de horarios --- */}
        <div
          style={{
            flex: "1",
            padding: "20px",
            background: "#f0f8ff",
            borderRadius: "8px",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h3 style={{ marginBottom: "20px", color: "#333" }}>Gestión de Horarios</h3>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Horarios Programados</h3>
            </div>

            {horariosGrupoActuales.length > 0 ? (
              <div className="space-y-3">
                {horariosGrupoActuales.map((horario, index) => (
                  <div
                    key={horario.idHorarioGrupo}
                    className={`group relative overflow-hidden rounded-xl border transition-all duration-300 ${
                      deletingIds.has(horario.idHorarioGrupo)
                        ? 'bg-red-50 border-red-200 opacity-50'
                        : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm ${
                            ['bg-gradient-to-br from-blue-500 to-blue-600', 
                             'bg-gradient-to-br from-green-500 to-green-600', 
                             'bg-gradient-to-br from-purple-500 to-purple-600',
                             'bg-gradient-to-br from-orange-500 to-orange-600',
                             'bg-gradient-to-br from-pink-500 to-pink-600'][index % 5]
                          }`}>
                            {horario.horario.dia?.nombre.substring(0, 2)}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center space-x-6">
                              <div>
                                <div className="font-semibold text-gray-800">{horario.horario.dia?.nombre}</div>
                                <div className="text-sm text-gray-500">
                                  {horario.horario.horaInicio} - {horario.horario.horaFin}
                                </div>
                              </div>
                              
                              <div className="flex items-center text-gray-600">
                                <span className="text-sm">Aula {horario.nroAula || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => eliminarHorarioIndividual(horario.idHorarioGrupo)}
                          disabled={deletingIds.has(horario.idHorarioGrupo)}
                          className="flex items-center px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100 disabled:opacity-50"
                        >
                          {deletingIds.has(horario.idHorarioGrupo) ? (
                            <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin mr-2" />
                          ) : (
                            <span>🗑️</span>
                          )}
                          <span className="text-sm font-medium ml-2">
                            {deletingIds.has(horario.idHorarioGrupo) ? 'Eliminando...' : 'Eliminar'}
                          </span>
                        </button>
                      </div>
                    </div>
                    
                    <div className={`h-1 bg-gradient-to-r ${
                      ['from-blue-400 to-blue-600', 
                       'from-green-400 to-green-600', 
                       'from-purple-400 to-purple-600',
                       'from-orange-400 to-orange-600',
                       'from-pink-400 to-pink-600'][index % 5]
                    }`} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                <h4 className="text-lg font-medium text-gray-600 mb-2">No hay horarios programados</h4>
                <p className="text-gray-500">Agrega tu primer horario para comenzar</p>
              </div>
            )}
          </div>

          <div className="border rounded-xl p-6 bg-white mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Agregar nuevo horario</h4>
            <NuevoHorarioFormCard control={control} register={registerHorarios} horarios={horarios} errors={errorsHorarios} />
          </div>

          <button type="button" 
            className="w-full px-8 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl font-semibold hover:from-green-500 hover:to-green-500 transition-all duration-300 transform hover:scale-105 shadow-lg"
            onClick={handleSubmitHorarios}>
            Actualizar Horarios
          </button>
        </div>
      </div>
    </ContenedorPageTemplate>
  );
};
